import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Plus, Package, Pencil, Power } from "lucide-react";
import { Loading, EmptyState, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const empty = { name: "", description: "", price: "", duration_days: "", deliverables: "", target_result: "", target_leads: "", active: true };

export default function Plans() {
  const [plans, setPlans] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);

  const load = useCallback(async () => setPlans((await api.get("/plans")).data), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setF({ ...p, price: p.price, duration_days: p.duration_days, target_leads: p.target_leads, deliverables: (p.deliverables || []).join("\n") }); setOpen(true); };

  const save = async () => {
    if (!f.name) { toast.error("Plan name required"); return; }
    const payload = {
      name: f.name, description: f.description, price: Number(f.price || 0),
      duration_days: Number(f.duration_days || 0),
      deliverables: (f.deliverables || "").split("\n").map((s) => s.trim()).filter(Boolean),
      target_result: f.target_result, target_leads: Number(f.target_leads || 0), active: f.active,
    };
    try {
      if (editing) await api.put(`/plans/${editing.id}`, payload);
      else await api.post("/plans", payload);
      toast.success("Plan saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (p) => { await api.put(`/plans/${p.id}`, { ...p, active: !p.active, deliverables: p.deliverables }); load(); };

  if (plans === null) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">Plans & Services</h1>
          <p className="text-sm text-slate-500 mt-1">Define service plans with pricing and deliverables.</p>
        </div>
        <Button data-testid="add-plan-button" onClick={openNew} className="bg-[#1e3a8a] hover:bg-[#1e40af]"><Plus className="h-4 w-4 mr-1.5" /> Add Plan</Button>
      </div>

      {plans.length === 0 ? (
        <div className="bg-white border rounded-xl"><EmptyState icon={Package} title="No plans yet" sub="Create your first service plan." /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.id} data-testid={`plan-card-${p.id}`} className={`bg-white border rounded-xl p-5 shadow-sm ${p.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
              <div className="flex items-start justify-between">
                <div className="font-semibold text-slate-900 font-display">{p.name}</div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => toggle(p)} className={`h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 ${p.active ? "text-emerald-600" : "text-slate-400"}`}><Power className="h-4 w-4" /></button>
                </div>
              </div>
              {p.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{p.description}</p>}
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-bold font-display text-slate-900"><Money value={p.price} /></span>
                {p.duration_days > 0 && <span className="text-sm text-slate-500">/ {p.duration_days} days</span>}
              </div>
              {p.target_leads > 0 && <div className="mt-1 text-sm text-blue-700">Target: {p.target_leads} leads</div>}
              {p.deliverables?.length > 0 && (
                <ul className="mt-3 space-y-1 text-sm text-slate-600 list-disc list-inside">
                  {p.deliverables.slice(0, 4).map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
              <div className="mt-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Active" : "Inactive"}</span></div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Plan" : "Add Plan"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Plan Name *</Label><Input data-testid="pf-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Description</Label><Textarea data-testid="pf-desc" value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" rows={2} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Price (₹)</Label><Input data-testid="pf-price" type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Duration (days)</Label><Input data-testid="pf-duration" type="number" value={f.duration_days} onChange={(e) => set("duration_days", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Target Leads</Label><Input data-testid="pf-leads" type="number" value={f.target_leads} onChange={(e) => set("target_leads", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div><Label>Deliverables (one per line)</Label><Textarea data-testid="pf-deliverables" value={f.deliverables} onChange={(e) => set("deliverables", e.target.value)} className="mt-1.5" rows={3} placeholder={"Daily lead report\nDedicated campaign manager"} /></div>
            <div><Label>Expected Result (optional)</Label><Input data-testid="pf-result" value={f.target_result} onChange={(e) => set("target_result", e.target.value)} className="mt-1.5" /></div>
            <div className="flex items-center justify-between rounded-lg border p-3"><div className="text-sm font-medium">Active</div><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="pf-active" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-plan" onClick={save} className="bg-[#1e3a8a]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
